import React, { useEffect, useState, useRef } from "react";
import { t } from "i18next";
import { Link } from "react-router-dom";
import { routeUrls } from "../../config/routeUrls";
import imageDocumentsDashboard from "../../assets/images/icons/documents-dashboard.svg";
import { tripsService } from "../../api/tripsService/tripsServices";
import { TripKPI, TripRequestBody } from "../../types/api";
import LinkButton from "../ui/linkButton/LinkButton";
import { BusinessProfile } from "../../types/profile";
import { useApiCache } from "../../utils/hooks/useApiCache";

interface WidgetTripsProps {
  businessProfile: BusinessProfile;
}

const WidgetTrips: React.FC<WidgetTripsProps> = ({ businessProfile }) => {
  const [tripsToBeAccepted, setTripsToBeAccepted] = useState<number>(0);
  const [tripsWaitingForDocumentation, setTripsWaitingForDocumentation] = useState<number>(0);
  const [tripsWithoutPlate, setTripsWithoutPlate] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  
  const { getCacheKey, isCached, getCachedData, setCachedData, isFetching, setFetching } = useApiCache();

  useEffect(() => {
    if (!businessProfile) {
      setIsLoading(false);
      return;
    }

    const fetchTripsCount = async (kpi: TripKPI) => {
      if (!businessProfile?.legacycodes || businessProfile.legacycodes.length === 0) {
        console.warn("No legacy codes found in business profile");
        return 0;
      }
      
      const body: TripRequestBody = {
        legacyCodes: businessProfile.legacycodes,
        kpi,
        agencyqualification: businessProfile.agencyqualification || null
      };

      const cacheKey = getCacheKey('tripsCount', businessProfile, { kpi });
      
      // Controlla se i dati sono in cache
      if (isCached(cacheKey)) {
        console.log(`WidgetTrips: Using cached data for KPI ${kpi}`);
        return getCachedData(cacheKey);
      }

      // Evita chiamate concorrenti
      if (isFetching(cacheKey)) {
        console.log(`WidgetTrips: Skipping fetch for KPI ${kpi} - already fetching`);
        return 0;
      }

      try {
        setFetching(cacheKey, true);
        console.log(`WidgetTrips: Fetching trips count for KPI ${kpi}`);
        const tripsResp = await tripsService.getTripsCount(body);
        const count = tripsResp.count;
        
        // Salva in cache
        setCachedData(cacheKey, count, businessProfile);
        console.log(`WidgetTrips: Cached trips count for KPI ${kpi}: ${count}`);
        
        return count;
      } catch (error) {
        console.error(`Error fetching trips count for KPI ${kpi}:`, error);
        return 0;
      } finally {
        setFetching(cacheKey, false);
      }
    };

    const loadAllTripsCounts = async () => {
      setIsLoading(true);
      
      try {
        const [toBeAccepted, waitingForDoc, withoutPlate] = await Promise.all([
          fetchTripsCount(TripKPI.ToBeAccepted),
          fetchTripsCount(TripKPI.WaitingForDocumentation),
          fetchTripsCount(TripKPI.WithoutPlate),
        ]);

        setTripsToBeAccepted(toBeAccepted || 0);
        setTripsWaitingForDocumentation(waitingForDoc || 0);
        setTripsWithoutPlate(withoutPlate || 0);
        console.log('WidgetTrips: All trips counts loaded successfully');
      } catch (error) {
        console.error('WidgetTrips: Error loading trips counts:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadAllTripsCounts();
  }, [businessProfile, getCacheKey, isCached, getCachedData, setCachedData, isFetching, setFetching]);

  const renderTripsCount = (count: number, kpi: TripKPI) => {
    return (
      <div className="py-4 border-b md:flex lg:block xl:flex md:justify-between md:items-center">
        <div>
          {<div className="font-black text-red text-[40px] leading-[53px]">{count}</div>}

          <p className="text-lg leading-[22px]">
            {count === 1
              ? t("screens.dashboard.widgets.trips.tripPrefixes.singular")
              : t("screens.dashboard.widgets.trips.tripPrefixes.plural")}{" "}
            <strong>{t(`screens.dashboard.widgets.trips.kpi.${kpi}`)}</strong>
          </p>
        </div>

        {count > 0 && (
          <LinkButton className="mt-4" href={routeUrls.trips + "?kpi=" + kpi}>
            {t("screens.dashboard.widgets.trips.button")}
          </LinkButton>
        )}
      </div>
    );
  };

  const renderSkeletonLoader = () => {
    return (
      <div className="py-4 border-b md:flex lg:block xl:flex md:justify-between md:items-center">
        <div>
          <div className="font-black text-red text-[40px] leading-[53px] animate-pulse bg-gray-200 h-[53px] w-16 rounded"></div>
          <div className="text-lg leading-[22px] animate-pulse bg-gray-200 h-6 w-48 rounded mt-2"></div>
        </div>
      </div>
    );
  };

  return (
    <div className="mb-[56px] md:mb-0 md:bg-lightGrey md:p-6 md:rounded card-shadow">
      <h2 className="font-light text-[24px] mb-4 sm:mb-0 leading-[38px]">
        {t("screens.dashboard.widgets.trips.heading")}
      </h2>
      
      {isLoading ? (
        <>
          {renderSkeletonLoader()}
          {renderSkeletonLoader()}
          {renderSkeletonLoader()}
        </>
      ) : (
        <>
          {renderTripsCount(tripsToBeAccepted, TripKPI.ToBeAccepted)}
          {renderTripsCount(tripsWithoutPlate, TripKPI.WithoutPlate)}
          {renderTripsCount(tripsWaitingForDocumentation, TripKPI.WaitingForDocumentation)}
        </>
      )}
      
      <div className="mt-6">
        <Link className="inline-flex items-center" to={routeUrls.trips}>
          <img src={imageDocumentsDashboard} alt="services_icon_bag" />
          <div className="uppercase text-sm leading-[17px] underline text-red font-semibold ml-[8px] mt-[2px]">
            {t("screens.dashboard.widgets.trips.link")}
          </div>
        </Link>
      </div>
    </div>
  );
};

export default WidgetTrips;
